import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import useResponsive from 'src/hooks/useResponsive';
// services
import {
    getChatInfoByChatId,
    setSelectedMessage,
    clearNewMessagesClienteByChatId,
} from 'src/redux/slices/chat';
import { useDispatch, useSelector } from 'src/redux/store';
// components
import Scrollbar from 'src/components/Scrollbar';
import ChatComponent from 'src/components/_chats/chat/components/ChatPage/ChatComponent';
import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import SkeletonChatWindow from 'src/components/skeleton/chats/SkeletonChatWindow';
import { Box } from '@mui/material';
import useAuth from 'src/hooks/useAuth';
import { groupMessagesByDate } from 'src/utils/chats.utils';
import ModalLightbox from 'src/@/components/shared/ModalLightbox';
import { removeMessageFromIDB, getImageBlob } from 'src/helpers/cache.helper';
import { useSnackbar } from 'notistack';
import { getAudioFromCache } from 'src/helpers/audio.helper';
import { toast } from 'src/@/components/ui/use-toast';
import ModalEncaminharMsg from './ChatComponent/ModalEncaminharMsg';
import ModalTratamentoIA from './ChatComponent/ModalTratamentoIA';
// actions
import v1_actionEnviarMenagemWhatsapp from 'src/actions/v1/whatsapp/v1_actionEnviarMenagemWhatsapp';
import v1_actionEnviarArquivoBuffer from 'src/actions/v1/whatsapp/v1_actionEnviarArquivoBuffer';
import { v2_actionEnviarAudioBuffer } from 'src/services/api/index';
import v2_actionDeletarNota from 'src/actions/v2/chats/v2_actionDeletarNota';
import v1_actionEncaminharMensagemWhatsapp from 'src/actions/v1/whatsapp/v1_actionEncaminharMensagemWhatsapp';
import v3_actionFeedbackAgent from 'src/actions/v3/ai/v3_actionFeedbackAgent';
import ChatAIAnalysisMessage from './ChatAIAnalysisMessage';
import { Checkbox } from 'src/@/components/ui/checkbox';
import { verifyFeatures } from 'src/utils/functions';
import NewMessagesButton from 'src/components/_chats/NewMessagesButton';

// ----------------------------------------------------------------------

const spinAnimation = `
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
`;

ChatMessageList.propTypes = {
    conversa: PropTypes.array.isRequired,
    chat: PropTypes.object,
    cliente: PropTypes.object,
    callbackGpt: PropTypes.func,
    loadingChat: PropTypes.bool,
    config: PropTypes.object,
    source: PropTypes.string,
    onCallbackResend: PropTypes.func,
    mensagens: PropTypes.array,
    sentimentAnalysis: PropTypes.array,
};

const Chat = ({
    messages,
    config,
    chatInfo,
    isDesktop,
    onCallbakOpenModal,
    selectMessageId,
    onDeleteNote,
    callbackGpt,
    resendMsg,
    loadingResend,
    onDeleteMsgAudioResend,
    onResendAudio,
    loadingResendAudio,
    selectMessageResponder,
    renderAnalysis,
    highlightedMessageId,
    firstMessageRef,
}) => {
    const { t } = useTranslation();
    const [selectedMessages, setSelectedMessages] = useState([]);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [openEncaminharDialog, setOpenEncaminharDialog] = useState(false);
    const [loadingEncaminhar, setLoadingEncaminhar] = useState(false);
    const [openTratamentoIADialog, setOpenTratamentoIADialog] = useState(false);
    const [loadingTratamentoIA, setLoadingTratamentoIA] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const { user, cliente } = useAuth();
    const MAX_MESSAGES = 8;

    const handleMessageSelect = (messageId) => {
        setIsSelectionMode(true);
        setSelectedMessages((prev) => {
            if (prev.includes(messageId)) {
                return prev.filter((id) => id !== messageId);
            }
            if (prev.length >= MAX_MESSAGES) {
                enqueueSnackbar(t('chat.messageList.maxMessagesReached', { count: MAX_MESSAGES }), {
                    variant: 'warning',
                    autoHideDuration: 3000,
                    anchorOrigin: { horizontal: 'center', vertical: 'top' },
                });
                return prev;
            }
            return [...prev, messageId];
        });
    };

    const handleEncaminharMensagens = async (phone) => {
        try {
            setLoadingEncaminhar(true);
            for (const messageId of selectedMessages) {
                const message = messages.find((msg) => msg.id === messageId);
                if (message) {
                    const data = { phone: phone, messageId: message.id };
                    const querys = `?sessao=${chatInfo.sessao}&canal=${chatInfo.canal}`;
                    await v1_actionEncaminharMensagemWhatsapp(querys, data);
                }
            }
            setLoadingEncaminhar(false);
            setOpenEncaminharDialog(false);
            setSelectedMessages([]);
            setIsSelectionMode(false);
            enqueueSnackbar(t('chat.messageList.forwardSuccess'), { variant: 'success' });
        } catch (error) {
            setLoadingEncaminhar(false);
            enqueueSnackbar(t('chat.messageList.forwardError'), { variant: 'error' });
            console.error(error);
        }
    };

    const handleEnviarParaIA = async (data) => {
        try {
            setLoadingTratamentoIA(true);
            const feedbackPayload = {
                user_id: user.id,
                cliente_id: chatInfo.cliente_id,
                chat_id: chatInfo._id,
                scope: {
                    messages: selectedMessages,
                    msg_wrong: data.respostaErrada,
                    msg_correct: data.respostaCorreta,
                },
            };
            await v3_actionFeedbackAgent(feedbackPayload);
            setLoadingTratamentoIA(false);
            setOpenTratamentoIADialog(false);
            setSelectedMessages([]);
            setIsSelectionMode(false);
            enqueueSnackbar(t('chat.messageList.aiFeedbackSuccess'), { variant: 'success' });
        } catch (error) {
            setLoadingTratamentoIA(false);
            enqueueSnackbar(t('chat.messageList.aiFeedbackError'), { variant: 'error' });
            console.error(error);
        }
    };

    const groupedMessages = groupMessagesByDate(messages);

    return (
        <div className="relative">
            <div>
                {Object.keys(groupedMessages).map((date) => (
                    <div key={date}>
                        <div className="w-full flex justify-center py-4">
                            <div className="px-3 py-1 bg-gray-50/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-100 dark:border-gray-700 rounded-full text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 shadow-sm dark:shadow-gray-900/20">
                                {date}
                            </div>
                        </div>
                        {groupedMessages[date].map((message, index) => {
                            if (message.type === 'analysis') {
                                return (
                                    <div key={`analysis-${message.id || index}`} className="w-full my-4">
                                        {renderAnalysis && message.analysisData ? (
                                            renderAnalysis(message.analysisData)
                                        ) : (
                                            <div className="text-center text-gray-500 dark:text-gray-400 text-sm">{t('chat.messageList.analysisUnavailable')}</div>
                                        )}
                                    </div>
                                );
                            }
                            const isFirstMessage = index === 0;
                            const isFirstGroup = Object.keys(groupedMessages).indexOf(date) === 0;
                            return (
                                <div
                                    id={`message-${message.id || `temp-${index}`}`}
                                    key={message.id || index}
                                    ref={isFirstMessage && isFirstGroup ? firstMessageRef : null}
                                    className={`relative flex ${message.fromMe ? 'justify-end' : 'justify-start'
                                        } mt-1 transition-all duration-500 ${highlightedMessageId === message.id ? 'bg-[#7C3AED]/10 ring-1 ring-[#7C3AED]/20 rounded-2xl p-2' : ''
                                        }`}
                                >
                                    {isSelectionMode && message.type !== 'analysis' && (
                                        <div className="flex justify-center items-center mr-2">
                                            <Checkbox
                                                checked={selectedMessages.includes(message.id)}
                                                onCheckedChange={() => handleMessageSelect(message.id)}
                                            />
                                        </div>
                                    )}
                                    <div
                                        className={`flex items-center ${message.type === 'analysis'
                                            ? 'justify-center w-full'
                                            : `${message.fromMe ? 'justify-end' : 'justify-start'} w-full ${selectedMessages.includes(message.id) ? 'bg-[#7C3AED]/5 ring-1 ring-[#7C3AED]/20 rounded-2xl' : ''
                                            }`
                                            }`}
                                    >
                                        <div className={`${isSelectionMode && message.type !== 'analysis' ? 'ml-8' : ''}`}>
                                            <ChatComponent
                                                loadingResendAudio={loadingResendAudio}
                                                onResendAudio={onResendAudio}
                                                onDeleteMsgAudioResend={onDeleteMsgAudioResend}
                                                config={config}
                                                chat={chatInfo}
                                                callbackGpt={(value) => {
                                                    callbackGpt(value);
                                                }}
                                                resendMsg={resendMsg}
                                                onDeleteNote={() => onDeleteNote(message.id)}
                                                isMe={message.fromMe ? 'right' : 'left'}
                                                message={message}
                                                onCallbakOpenModal={onCallbakOpenModal}
                                                selectMessageResponder={() => selectMessageResponder(message)}
                                                selectMessageId={(messageId) => handleMessageSelect(messageId)}
                                                isDesktop={isDesktop}
                                                loadingResend={loadingResend}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* Botão flutuante para encaminhar */}
            {selectedMessages.length > 0 && (
                <div className="fixed bottom-32 left-1/2 transform -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border border-gray-200/50 dark:border-gray-700/50 rounded-2xl shadow-xl shadow-gray-200/50 dark:shadow-gray-900/50 p-2 flex items-center gap-3 pr-4">
                        <div className="pl-3 pr-2 border-r border-gray-100 dark:border-gray-700">
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                                <span className="text-[#7C3AED] font-bold">{selectedMessages.length}</span>
                                <span className="text-gray-400 dark:text-gray-500 mx-1">/</span> {MAX_MESSAGES}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => {
                                    setSelectedMessages([]);
                                    setIsSelectionMode(false);
                                }}
                                className="hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-2 rounded-xl transition-colors"
                                title={t('chat.messageList.clearSelection')}
                            >
                                <Iconify icon="material-symbols:close" />
                            </button>
                            <div className="h-4 w-px bg-gray-100 dark:bg-gray-700" />
                            <button
                                onClick={() => setOpenEncaminharDialog(true)}
                                disabled={loadingEncaminhar}
                                className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white px-3 py-2 rounded-xl flex items-center gap-2 text-xs font-medium shadow-sm shadow-[#7C3AED]/20 transition-all hover:scale-105 active:scale-95"
                            >
                                {loadingEncaminhar ? <Iconify icon="svg-spinners:ring-resize" /> : <Iconify icon="material-symbols:forward" />}
                                <span>{t('chat.messageList.forward')}</span>
                            </button>
                            {verifyFeatures(cliente.features, 'ai.feedback') && (
                                <button
                                    onClick={() => setOpenTratamentoIADialog(true)}
                                    disabled={loadingTratamentoIA}
                                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-2 rounded-xl flex items-center gap-2 text-xs font-medium transition-all hover:scale-105 active:scale-95"
                                >
                                    {loadingTratamentoIA ? (
                                        <Iconify icon="svg-spinners:ring-resize" />
                                    ) : (
                                        <Iconify icon="material-symbols:psychology" className="text-[#7C3AED]" />
                                    )}
                                    <span>{t('chat.messageList.aiFeedback')}</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Encaminhar Mensagem */}
            {openEncaminharDialog && (
                <ModalEncaminharMsg
                    user={user}
                    open={openEncaminharDialog}
                    loading={loadingEncaminhar}
                    onSubmit={handleEncaminharMensagens}
                    onClose={() => setOpenEncaminharDialog(false)}
                />
            )}

            {/* Modal de Tratamento de IA */}
            {openTratamentoIADialog && (
                <ModalTratamentoIA
                    user={user}
                    open={openTratamentoIADialog}
                    loading={loadingTratamentoIA}
                    onSubmit={handleEnviarParaIA}
                    onClose={() => setOpenTratamentoIADialog(false)}
                />
            )}
        </div>
    );
};

export default function ChatMessageList({
    conversa,
    chat,
    cliente,
    callbackGpt,
    loadingChat,
    config,
    source,
    onCallbackResend,
    mensagens,
    sentimentAnalysis,
}) {
    const { t } = useTranslation();
    const scrollRef = useRef();
    const chatRef = useRef(null);
    const messagesEnd = useRef(null);
    const firstMessageRef = useRef(null);
    const dispatch = useDispatch();
    const isDesktop = useResponsive('up', 'md');
    const [setFiles] = useState([]);
    const { chatInfo, newMessagesCliente } = useSelector((state) => state.chat);
    const [clickedUrl, setClickedUrl] = useState('');
    const [openModalImage, setOpenModalImage] = useState(false);
    const [loadingResend, setLoadingResend] = useState(false);
    const [loadingResendAudio, setLoadingResendAudio] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();
    const [highlightedMessageId, setHighlightedMessageId] = useState(null);
    const [isNavigatingToMessage, setIsNavigatingToMessage] = useState(false);
    const [lastMessageCount, setLastMessageCount] = useState(0);
    const [isAtBottom, setIsAtBottom] = useState(true);

    // Estados para paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
    const [allMessages, setAllMessages] = useState([]);

    function toTimestamp(date) {
        if (!date) return 0;
        try {
            return Math.floor(new Date(date).getTime() / 1000);
        } catch (error) {
            console.error('Erro ao converter data:', date, error);
            return 0;
        }
    }

    const loadMoreMessages = async () => {
        if (loadingMoreMessages || !hasNextPage || !chat?.sessao || !chat?.numeroCliente) {
            return;
        }
        try {
            setLoadingMoreMessages(true);
            setIsAtBottom(false);
            const scrollContainer = scrollRef.current;
            const previousScrollHeight = scrollContainer?.scrollHeight || 0;
            const previousScrollTop = scrollContainer?.scrollTop || 0;

            const nextPage = currentPage + 1;
            const querys = `?sessao=${chat.sessao}&numero=${chat.numeroCliente}&canal=whatsapp&page=${nextPage}&limit=50&chat_source=db`;

            const { default: v2_actionBuscarMensagens } = await import('src/actions/v2/whatsapp/v2_actionBuscarMensagens');
            const response = await v2_actionBuscarMensagens(querys);

            if (response.data && response.data.status === 'success') {
                const { data: newMessages, pagination: newPagination } = response.data;
                if (newMessages && newMessages.length > 0) {
                    setAllMessages((prevMessages) => [...newMessages, ...prevMessages]);
                    setCurrentPage(nextPage);
                    setHasNextPage(newPagination.hasNextPage);

                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            if (scrollContainer) {
                                const newScrollHeight = scrollContainer.scrollHeight;
                                const heightDifference = newScrollHeight - previousScrollHeight;
                                const newScrollTop = previousScrollTop + heightDifference;
                                const maxScrollTop = newScrollHeight - scrollContainer.clientHeight;
                                const finalScrollTop = Math.min(newScrollTop, maxScrollTop);
                                scrollContainer.scrollTop = finalScrollTop;
                                setIsAtBottom(finalScrollTop + scrollContainer.clientHeight >= newScrollHeight - 100);
                            }
                        });
                    });
                } else {
                    setHasNextPage(false);
                }
            } else {
                setHasNextPage(false);
            }
        } catch (error) {
            console.error('Erro ao carregar mais mensagens:', error);
            enqueueSnackbar(t('chat.messageList.loadPreviousError'), {
                variant: 'error',
                autoHideDuration: 3000,
                anchorOrigin: { horizontal: 'center', vertical: 'top' },
            });
        } finally {
            setLoadingMoreMessages(false);
        }
    };

    useEffect(() => {
        if (chat?._id) {
            setAllMessages(conversa || []);
            setCurrentPage(1);
            setHasNextPage(true);
            setIsAtBottom(true);
        }

    }, [chat?._id]);

    useEffect(() => {
        if (chat?._id) {
            setIsAtBottom(true);
            setIsNavigatingToMessage(false);
            setHighlightedMessageId(null);
            dispatch(clearNewMessagesClienteByChatId(chat._id));

            const forceScrollToBottom = () => {
                if (scrollRef.current) {
                    const scrollContainer = scrollRef.current;
                    scrollContainer.scrollTop = scrollContainer.scrollHeight;
                    setIsAtBottom(true);
                }
            };

            forceScrollToBottom();
            setTimeout(forceScrollToBottom, 50);
            setTimeout(forceScrollToBottom, 150);
            setTimeout(forceScrollToBottom, 300);
        }
    }, [chat?._id, dispatch]);

    const conversaArray = Array.isArray(conversa) ? [...conversa] : [];
    const menorTimestamp =
        conversaArray.length > 0
            ? Math.min(...conversaArray.map((m) => m.t || m.timestamp || toTimestamp(m.date) || 0))
            : 0;
    const maiorTimestamp =
        conversaArray.length > 0
            ? Math.max(...conversaArray.map((m) => m.t || m.timestamp || toTimestamp(m.date) || 0))
            : 0;

    const notesChat = (chatInfo?.notas || []).filter((nota) => {
        if (!nota || !nota.date) return false;
        const notaTimestamp = toTimestamp(nota.date);
        if (conversaArray.length === 0) return true;
        return notaTimestamp >= menorTimestamp;
    });

    const filteredSentimentAnalysis = (sentimentAnalysis || []).filter((analysis) => {
        if (!analysis) return false;
        const analysisTimestamp = analysis.last_analisys_timestamp
            ? parseInt(analysis.last_analisys_timestamp)
            : toTimestamp(analysis.date);
        if (conversaArray.length === 0) return true;
        const tolerance = 24 * 60 * 60;
        return analysisTimestamp >= menorTimestamp - tolerance && analysisTimestamp <= maiorTimestamp + tolerance;
    });

    let messagesChat = [...conversaArray];
    if (allMessages.length > 0) {
        const conversaIds = new Set(conversaArray.map((m) => m.id));
        allMessages.forEach((msg) => {
            if (!conversaIds.has(msg.id)) {
                messagesChat.push(msg);
            }
        });
    }

    const seenIds = new Set();
    messagesChat = messagesChat.filter((m) => {
        const key = m.id || `${m.type}-${m.t || m.timestamp}`;
        if (seenIds.has(key)) return false;
        seenIds.add(key);
        return true;
    });

    notesChat.forEach((nota) => {
        const novaNota = {
            id: nota.id || `note-${Date.now()}-${Math.random()}`,
            body: nota.body,
            usuario_id: nota.usuario_id,
            date: nota.date,
            timestamp: toTimestamp(nota.date),
            t: toTimestamp(nota.date),
            type: 'note',
            fromMe: true,
            userName: nota.userName || nota.usuario_nome || t('chat.messageList.user'),
        };
        messagesChat.push(novaNota);
    });

    if (filteredSentimentAnalysis && Array.isArray(filteredSentimentAnalysis)) {
        filteredSentimentAnalysis.forEach((analysis) => {
            const timestamp = analysis.last_analisys_timestamp
                ? parseInt(analysis.last_analisys_timestamp)
                : Math.floor(new Date(analysis.date).getTime() / 1000);
            const analysisMessage = {
                id: `analysis-${analysis._id || Date.now()}`,
                type: 'analysis',
                timestamp: timestamp,
                t: timestamp,
                analysisData: analysis,
                fromMe: true,
            };
            messagesChat.push(analysisMessage);
        });
    }

    messagesChat = messagesChat.sort((a, b) => (a.t || 0) - (b.t || 0));

    const scrollMessagesToBottom = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            setIsAtBottom(true);
        }
    };

    const scrollMessagesToBottomSmooth = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
            setTimeout(() => setIsAtBottom(true), 150);
        }
    };

    const checkIfAtBottom = () => {
        if (scrollRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
            const threshold = 100;
            const isBottom = scrollTop + clientHeight >= scrollHeight - threshold;
            setIsAtBottom(isBottom);
            if (isBottom && chat?._id) {
                const countNewForChat = newMessagesCliente.filter((msg) => msg.chatId === chat._id).length;
                if (countNewForChat > 0) {
                    dispatch(clearNewMessagesClienteByChatId(chat._id));
                }
            }
        }
    };

    const handleScrollToBottom = () => {
        scrollMessagesToBottomSmooth();
        if (chat?._id) {
            dispatch(clearNewMessagesClienteByChatId(chat._id));
        }
        setIsAtBottom(true);
    };

    const navigateToMessageById = (messageId) => {
        setHighlightedMessageId(messageId);
        setIsNavigatingToMessage(true);

        setTimeout(() => {
            const messageElement = document.getElementById(`message-${messageId}`);
            if (messageElement) {
                const container = scrollRef.current;
                if (container) {
                    const elementRect = messageElement.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();
                    const relativeTop = elementRect.top - containerRect.top;
                    const scrollTop = container.scrollTop + relativeTop - containerRect.height / 2;
                    container.scrollTo({ top: scrollTop, behavior: 'smooth' });
                }
                setTimeout(() => {
                    setHighlightedMessageId(null);
                }, 3000);
                setTimeout(() => {
                    setIsNavigatingToMessage(false);
                }, 10000);
            } else {
                setHighlightedMessageId(null);
                setIsNavigatingToMessage(false);
            }
        }, 200);
    };

    useEffect(() => {
        const currentMessagesCount = messagesChat.length;
        if (currentMessagesCount > lastMessageCount && !highlightedMessageId && !isNavigatingToMessage && !loadingMoreMessages) {
            const lastMessage = messagesChat[currentMessagesCount - 1];
            const isAgentMessage = lastMessage?.fromMe === true;
            if (isAgentMessage) {
                scrollMessagesToBottom();
                setIsAtBottom(true);
                if (chat?._id) {
                    dispatch(clearNewMessagesClienteByChatId(chat._id));
                }
            } else if (isAtBottom) {
                scrollMessagesToBottom();
                if (chat?._id) {
                    dispatch(clearNewMessagesClienteByChatId(chat._id));
                }
            }
            setLastMessageCount(currentMessagesCount);
        } else if (currentMessagesCount !== lastMessageCount) {
            setLastMessageCount(currentMessagesCount);
        }
    }, [
        messagesChat.length,
        highlightedMessageId,
        isNavigatingToMessage,
        lastMessageCount,
        isAtBottom,
        loadingMoreMessages,
        chat?._id,
        dispatch,
        messagesChat,
    ]);

    useEffect(() => {
        const handleNavigateToMessage = (event) => {
            const { messageId } = event.detail;
            navigateToMessageById(messageId);
        };
        window.addEventListener('navigateToMessage', handleNavigateToMessage);
        return () => {
            window.removeEventListener('navigateToMessage', handleNavigateToMessage);
        };
    }, []);

    useEffect(() => {
        const scrollContainer = scrollRef.current;
        if (scrollContainer) {
            const handleScroll = () => {
                checkIfAtBottom();
            };
            setTimeout(() => {
                checkIfAtBottom();
            }, 100);
            scrollContainer.addEventListener('scroll', handleScroll);
            return () => {
                scrollContainer.removeEventListener('scroll', handleScroll);
            };
        }
    }, [newMessagesCliente.length, chat?._id]);

    async function handleDeleteNote(id) {
        try {
            await v2_actionDeletarNota(id);
            dispatch(getChatInfoByChatId(chat._id, null, user.id, false));
        } catch (error) {
            console.error(error);
        }
    }

    async function resendMsg(msg) {
        if (msg.type === 'audio' || msg.type === 'ptt') {
            await getAudioFromCache(msg.id);
            return;
        }
        if (msg && (msg.type === 'image' || msg.type === 'video' || msg.type === 'document')) {
            try {
                setLoadingResend(true);
                const blobUrl = await getImageBlob(msg.id);
                if (!blobUrl) {
                    enqueueSnackbar(t('chat.messageList.mediaNotInCache'), {
                        variant: 'warning',
                        autoHideDuration: 5000,
                        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
                    });
                    setLoadingResend(false);
                    return;
                }
                const response = await fetch(blobUrl);
                const blob = await response.blob();
                const formData = new FormData();
                const mime = msg.mimetype || blob.type || 'application/octet-stream';
                const ext = (mime.split('/')[1] || 'bin').split(';')[0];
                const filename = `${new Date().getTime()}.${ext}`;
                formData.append('filename', filename);
                formData.append('file', blob);
                formData.append('phone', chat.numeroCliente);
                if (msg.type === 'document') {
                    formData.append('isDocument', true);
                }
                const querys = `?sessao=${chat.sessao}&canal=whatsapp`;
                await v1_actionEnviarArquivoBuffer(querys, formData);
                await removeMessageFromIDB('error_msg_send', msg.id);
                onCallbackResend();
                setLoadingResend(false);
            } catch (error) {
                setLoadingResend(false);
                console.error('error', error);
                enqueueSnackbar(t('chat.messageList.resendMediaError'), {
                    variant: 'error',
                    autoHideDuration: 5000,
                    anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
                });
            }
            return;
        }
        if (!msg) return;
        try {
            setLoadingResend(true);
            const querys = `?sessao=${chat.sessao}&canal=whatsapp`;
            const body = { phone: chat.numeroCliente, message: msg.body };
            await v1_actionEnviarMenagemWhatsapp(querys, body);
            await removeMessageFromIDB('error_msg_send', msg.id);
            onCallbackResend();
            setLoadingResend(false);
        } catch (error) {
            setLoadingResend(false);
            console.error('error', error);
            enqueueSnackbar(t('chat.errors.sendFailed'), {
                variant: 'error',
                autoHideDuration: 5000,
                anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
            });
        }
    }

    function deleteMsgAudioResend(msg) {
        removeMessageFromIDB('error_msg_send', msg.id);
        removeMessageFromIDB('error_send_audio', msg.id);
        onCallbackResend();
    }

    const resendAudio = async (messageId) => {
        try {
            setLoadingResendAudio(true);
            const audioUrl = await getAudioFromCache(messageId);
            if (!audioUrl) {
                throw new Error(t('chat.messageList.audioNotInCache'));
            }
            const response = await fetch(audioUrl);
            const audioBlob = await response.blob();
            const formData = new FormData();
            const filename = `${new Date().getTime()}.ogg`;
            formData.append('filename', filename);
            formData.append('file', audioBlob);
            formData.append('phone', chat.numeroCliente);
            const querys = `?sessao=${chat.sessao}&canal=${chat.canal}`;
            await v2_actionEnviarAudioBuffer(querys, formData);
            await removeMessageFromIDB('error_msg_send', messageId);
            setLoadingResendAudio(false);
            toast({ description: t('chat.messageList.resendAudioSuccess') });
        } catch (err) {
            setLoadingResendAudio(false);
            console.error('Erro ao reenviar áudio:', err);
            toast({ variant: 'destructive', description: t('chat.messageList.resendAudioError') });
        }
    };

    const { getInputProps } = useDropzone({
        accept: ['image/*', 'application/*'],
        onDrop: (acceptedFiles) => {
            setFiles(acceptedFiles.map((file) => Object.assign(file, { preview: URL.createObjectURL(file) })));
        },
    });

    const currentChatNewMessages = newMessagesCliente.filter((msg) => msg.chatId === chat?._id);
    const newMessagesCount = currentChatNewMessages.length;

    return (
        <>
            <style>{spinAnimation}</style>
            <Scrollbar scrollableNodeProps={{ ref: scrollRef }} sx={{ p: 3, pb: 20 }}>
                <input {...getInputProps()} />
                <ModalLightbox open={openModalImage} onClose={() => setOpenModalImage(false)} src={clickedUrl} />
                <ul ref={chatRef}>
                    <div className="flex justify-center items-center py-4">
                        {hasNextPage ? (
                            <Button
                                onClick={loadMoreMessages}
                                disabled={loadingMoreMessages}
                                className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-full px-6 py-2 text-[12px] font-semibold shadow-lg shadow-[#7C3AED]/25 hover:shadow-xl hover:shadow-[#7C3AED]/30 transition-all duration-300 hover:-translate-y-0.5 disabled:bg-gray-200 dark:disabled:bg-gray-700 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:shadow-none"
                            >
                                {loadingMoreMessages ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        <span className="ml-2">{t('chat.messageList.loadingMessages')}</span>
                                    </>
                                ) : (
                                    <>
                                        <Iconify icon="mdi:arrow-up" className="w-4 h-4" />
                                        <span className="ml-2">{t('chat.messageList.loadPrevious')}</span>
                                    </>
                                )}
                            </Button>
                        ) : allMessages.length > 0 ? (
                            <div className="flex items-center gap-2 px-5 py-3 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                                <Iconify icon="mdi:check-circle" className="w-4 h-4 text-green-500 dark:text-green-400" />
                                <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">{t('chat.messageList.allMessagesLoaded')}</span>
                            </div>
                        ) : null}
                    </div>

                    {loadingChat ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <SkeletonChatWindow />
                        </Box>
                    ) : (
                        <Chat
                            loadingResendAudio={loadingResendAudio}
                            onDeleteMsgAudioResend={deleteMsgAudioResend}
                            onResendAudio={resendAudio}
                            messages={messagesChat}
                            resendMsg={resendMsg}
                            loadingResend={loadingResend}
                            chatInfo={chatInfo}
                            config={config}
                            isDesktop={isDesktop}
                            callbackGpt={callbackGpt}
                            onDeleteNote={(value) => handleDeleteNote(value)}
                            onCallbakOpenModal={(image) => {
                                setClickedUrl(image);
                                setOpenModalImage(true);
                            }}
                            selectMessageResponder={(message) => {
                                if (!isNavigatingToMessage) {
                                    scrollMessagesToBottom();
                                }
                                dispatch(setSelectedMessage({ message: message }));
                            }}
                            highlightedMessageId={highlightedMessageId}
                            firstMessageRef={firstMessageRef}
                            renderAnalysis={(analysisData) => {
                                if (!analysisData) return null;
                                return <ChatAIAnalysisMessage data={analysisData} user={user} />;
                            }}
                        />
                    )}
                    <div ref={messagesEnd} />
                </ul>
            </Scrollbar>
            <NewMessagesButton
                newMessagesCount={newMessagesCount}
                onScrollToBottom={handleScrollToBottom}
                isVisible={!isAtBottom && newMessagesCount > 0}
            />
        </>
    );
}